import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { customerbasedshift } from '../../Services/app/companyservice';
import { shiftadd } from '../../Services/app/masterservice';
import { useMachineGroups } from '../../Shared/hooks/useMachineGroups';
import PlantFilterSelect from '../../Shared/components/PlantFilterSelect';
import { getShiftTimes as getShiftTimesUtil } from '../../Shared/utils/holidaycalculation';
import {
  getComponentReportOverviewPdf,
  getComponentReportOverviewPpt,
  getProgramOverviewPdf,
  getProgramOverviewPpt,
  getProgramTransactionOverviewPdf,
} from '../../Services/app/oeeservice';
import MachineHourRateSettings from './components/settings/MachineHourRateSettings';
import ComponentCostSettings from './components/settings/ComponentCostSettings';
import JobCostSettings from './components/settings/JobCostSettings';
import OverviewTab from './tabs/OverviewTab';
import ComponentReportTab from './tabs/ComponentReportTab';
import ProgramOverviewTab from './tabs/ProgramOverviewTab';
import ProgramComponentReportTab from './tabs/ProgramComponentReportTab';
import ProgramTransactionReportTab from './tabs/ProgramTransactionReportTab';
import ProgramTransactionOverviewTab from './tabs/ProgramTransactionOverviewTab';
import { getStartAndEndTime } from './utils/programTimeRange';


const DEFAULT_MACHINE_HOUR_RATE_KEY = 'insightsDefaultMachineHourRate';


const INSIGHTS_METHODS = [4];

// INSIGHTS_METHODS as '1'..'4' strings (the value format costingMethod uses). Falls back to Method 4 (highest).
const ENABLED_METHODS = (() => {
  const valid = [...new Set((INSIGHTS_METHODS || []).map(String))]
    .filter(m => ['1', '2', '3', '4'].includes(m))
    .sort();
  return valid.length ? valid : ['4'];
})();

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'table', label: 'Table' },
];

const getCurrentShift = (shifts) => {
  if (!Array.isArray(shifts) || shifts.length === 0) return 'allshift';
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  for (const s of shifts) {
    const [fromH, fromM] = s.start_time.split(':').map(Number);
    const [toH, toM] = s.end_time.split(':').map(Number);
    const fromMinutes = fromH * 60 + fromM;
    const toMinutes = toH * 60 + toM;
    if (
      (fromMinutes <= currentMinutes && currentMinutes < toMinutes) ||
      (fromMinutes > toMinutes && (currentMinutes >= fromMinutes || currentMinutes < toMinutes))
    ) {
      return String(s.shift_no);
    }
  }
  return 'allshift';
};

export default function Insights4() {
  const customerId = localStorage.getItem('CustomerID');

  const [shifts, setShifts] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'week'));
  const [endDate, setEndDate] = useState(dayjs());
  const [isLoading, setIsLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const hasAutoSubmitted = useRef(false);
  const [activeTab, setActiveTab] = useState('overview');
  // Highest configured method is the default on load.
  const [costingMethod, setCostingMethod] = useState(ENABLED_METHODS[ENABLED_METHODS.length - 1]);

  // Method 3 is the Insights1 program-report pipeline ported in as an alternate
  // mode - single-machine selection, no shift filter, machine_name + time-window
  // based API calls instead of shift/date/method based ones.
  const isProgramMethod = costingMethod === '3';
  // Method 4: program_transaction -> pms_program_report drilldown (new
  // tb-node-mtlinki tables/endpoints). Reuses Method 3's single-machine
  // dropdown + time-window filters; no overview summary or PDF/PPT export.
  const isMethod4 = costingMethod === '4';
  // Single-select Machines dropdown used only in Method 3, mirroring Insights1's
  // machine selection (independent of the multi-select `selectedMachines` above).
  const [programMachine, setProgramMachine] = useState('');

  const [costSettingsOpen, setCostSettingsOpen] = useState(false);
  const [costSettingsTab, setCostSettingsTab] = useState('machine');
  const [defaultMachineHourRate, setDefaultMachineHourRate] = useState('');

  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        const result = await customerbasedshift(customerId, DEFAULT_MACHINE_HOUR_RATE_KEY);
        const value = result?.[0]?.value;
        if (value !== undefined && value !== null) {
          setDefaultMachineHourRate(String(value));
        } else {
          setDefaultMachineHourRate('240');
          try {
            await shiftadd({ [DEFAULT_MACHINE_HOUR_RATE_KEY]: '240' }, customerId, 'SERVER_SCOPE');
          } catch (saveErr) {
            console.error('Failed to seed default machine hourly rate', saveErr);
          }
        }
      } catch (err) {
        console.error('Failed to fetch default machine hourly rate', err);
        setDefaultMachineHourRate('240');
      }
    })();
  }, [customerId]);

  const {
    machineGroups,
    availableMachines,
    selectedMachines,
    selectedGroups,
    isAllMachinesSelected,
    showMachineGroupsDropdown,
    handleGroupChange,
    handleMachineChange,
    plantOptions,
    selectedPlants,
    showPlantFilter,
    handlePlantChange,
  } = useMachineGroups(customerId);

  useEffect(() => {
    hasAutoSubmitted.current = false;
  }, [customerId]);

  // Default the Method 3/4 single-select Machines dropdown to the first machine
  // whenever the available machine list (re)loads.
  useEffect(() => {
    if (availableMachines.length > 0) {
      setProgramMachine(availableMachines[0]);
    }
  }, [availableMachines]);

  const programMachinesArray = useMemo(() => {
    if (programMachine === 'all') return availableMachines;
    return programMachine ? [programMachine] : [];
  }, [programMachine, availableMachines]);

  const getShiftParam = useCallback((selected, shiftList) => {
    if (!shiftList || shiftList.length === 0) return '1,2';
    if (!selected || selected.length === 0 || selected.includes('all') || selected.length === shiftList.length) {
      return shiftList.map((s) => String(s.shift_no)).join(',');
    }
    return selected.filter((s) => s !== 'all').join(',');
  }, []);

  useEffect(() => {
    if (
      !hasAutoSubmitted.current &&
      selectedMachines.length > 0 &&
      selectedShifts.length > 0 &&
      programMachinesArray.length > 0
    ) {
      hasAutoSubmitted.current = true;
      const { startTimeMs, endTimeMs } = getStartAndEndTime(startDate, endDate, shifts);
      setAppliedFilters({
        groups: selectedGroups,
        machines: selectedMachines,
        programMachines: programMachinesArray,
        shift: getShiftParam(selectedShifts, shifts),
        startDate: startDate.format('DD-MM-YYYY'),
        endDate: endDate.format('DD-MM-YYYY'),
        startDateISO: startDate.format('YYYY-MM-DD'),
        endDateISO: endDate.format('YYYY-MM-DD'),
        startTimeMs,
        endTimeMs,
      });
    }
  }, [selectedMachines, selectedShifts, selectedGroups, startDate, endDate, shifts, getShiftParam, programMachinesArray]);

  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        const result = await customerbasedshift(customerId, 'allShift');
        const list = result[0]?.value || [];
        setShifts(list);
        if (list.length > 0) {
          setSelectedShifts(list.map((s) => String(s.shift_no)));
        }
      } catch (err) {
        console.error('Failed to fetch shifts', err);
        setShifts([]);
      }
    })();
  }, [customerId]);

  useEffect(() => {
    if (shifts.length > 0 && selectedShifts.length === 0) {
      setSelectedShifts(shifts.map((s) => String(s.shift_no)));
    }
  }, [shifts, selectedShifts]);

  const isAllShiftsSelected = shifts.length > 0 && selectedShifts.length === shifts.length;

  const handleShiftChange = (values) => {
    if (values.includes('all')) {
      setSelectedShifts(isAllShiftsSelected ? [] : shifts.map((s) => String(s.shift_no)));
      return;
    }
    setSelectedShifts(values);
  };

  const shiftOptions = useMemo(() => {
    const now = Date.now();
    const isToday = startDate.isSame(dayjs(), 'day');
    return shifts.map((shift) => {
      const isFuture = isToday && (() => {
        const times = getShiftTimesUtil(shifts, String(shift.shift_no), startDate);
        return times.from !== null && times.from > now;
      })();
      return (
        <MenuItem key={shift.shift_no} value={String(shift.shift_no)} disabled={isFuture}>
          <Checkbox checked={selectedShifts.includes(String(shift.shift_no))} disabled={isFuture} />
          <ListItemText primary={`${shift.shift_no}${isFuture ? ' (upcoming)' : ''}`} />
        </MenuItem>
      );
    });
  }, [shifts, startDate, selectedShifts]);

  const handleSubmit = useCallback(() => {
    setIsLoading(true);
    const { startTimeMs, endTimeMs } = getStartAndEndTime(startDate, endDate, shifts);
    setAppliedFilters({
      groups: selectedGroups,
      machines: selectedMachines,
      programMachines: programMachinesArray,
      shift: getShiftParam(selectedShifts, shifts),
      startDate: startDate.format('DD-MM-YYYY'),
      endDate: endDate.format('DD-MM-YYYY'),
      startDateISO: startDate.format('YYYY-MM-DD'),
      endDateISO: endDate.format('YYYY-MM-DD'),
      startTimeMs,
      endTimeMs,
    });
    setIsLoading(false);
  }, [selectedGroups, selectedMachines, selectedShifts, startDate, endDate, shifts, getShiftParam, programMachinesArray]);

  const [isExporting, setIsExporting] = useState(false);
  const [isExportingPpt, setIsExportingPpt] = useState(false);

  const handleExportOverviewPdf = useCallback(async () => {
    if (!appliedFilters) return;
    setIsExporting(true);
    try {
      const blob = isMethod4
        ? await getProgramTransactionOverviewPdf(appliedFilters.programMachines, appliedFilters.startTimeMs, appliedFilters.endTimeMs)
        : isProgramMethod
        ? await getProgramOverviewPdf(appliedFilters.programMachines, appliedFilters.startTimeMs, appliedFilters.endTimeMs)
        : await getComponentReportOverviewPdf(
            appliedFilters.machines,
            appliedFilters.shift,
            appliedFilters.startDateISO,
            appliedFilters.endDateISO,
            costingMethod
          );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `insights4-overview-${startDate.format('DDMMYYYY')}-${endDate.format('DDMMYYYY')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download Insights4 overview PDF', err);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Failed to download report. Check your internet connection and try again.',
      });
    } finally {
      setIsExporting(false);
    }
  }, [appliedFilters, startDate, endDate, costingMethod, isProgramMethod, isMethod4]);

  const handleExportOverviewPpt = useCallback(async () => {
    if (!appliedFilters) return;
    setIsExportingPpt(true);
    try {
      const blob = isProgramMethod
        ? await getProgramOverviewPpt(appliedFilters.programMachines, appliedFilters.startTimeMs, appliedFilters.endTimeMs)
        : await getComponentReportOverviewPpt(
            appliedFilters.machines,
            appliedFilters.shift,
            appliedFilters.startDateISO,
            appliedFilters.endDateISO,
            costingMethod
          );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `insights4-overview-${startDate.format('DDMMYYYY')}-${endDate.format('DDMMYYYY')}.pptx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download Insights4 overview PPT', err);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Failed to download report. Check your internet connection and try again.',
      });
    } finally {
      setIsExportingPpt(false);
    }
  }, [appliedFilters, startDate, endDate, costingMethod, isProgramMethod]);

  // Method 3 (Insights1 program-report pipeline) components expect
  // `filters.machines` to be the single-select machine list, not Method 1/2's
  // multi-select `appliedFilters.machines` - reshape without touching the
  // shared appliedFilters object the other tabs/settings rely on.
  const programFilters = useMemo(() => {
    if (!appliedFilters) return null;
    return {
      groups: appliedFilters.groups,
      machines: appliedFilters.programMachines,
      shift: appliedFilters.shift,
      startDate: appliedFilters.startDate,
      endDate: appliedFilters.endDate,
      startDateISO: appliedFilters.startDateISO,
      endDateISO: appliedFilters.endDateISO,
      startTimeMs: appliedFilters.startTimeMs,
      endTimeMs: appliedFilters.endTimeMs,
    };
  }, [appliedFilters]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', background: '#fefcfcff' }}>
      <Box sx={{ padding: '20px 30px', borderBottom: '1px solid #e0e0e0', background: '#ffffff', flexShrink: 0, marginTop: '30px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <h4 style={{ margin: 0 }}><b>Insights</b></h4>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            {showPlantFilter && (
              <PlantFilterSelect plantOptions={plantOptions} selectedPlants={selectedPlants} onChange={handlePlantChange} />
            )}
            {showMachineGroupsDropdown && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '13px', color: '#86868b' }}>Machine Group</InputLabel>
                <Select
                  multiple
                  value={selectedGroups}
                  onChange={(e) => handleGroupChange(e.target.value)}
                  label="Machine Group"
                  renderValue={(selected) => {
                    if (selected.length === machineGroups.length) return 'All Groups';
                    if (selected.length === 0) return 'Select Groups';
                    return selected.slice(0, 2).join(', ') + (selected.length > 2 ? '...' : '');
                  }}
                  sx={{
                    fontSize: '14px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      '&:hover': { borderColor: '#007AFF' },
                      '&.Mui-focused': { borderColor: '#007AFF', boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.1)' },
                    },
                  }}
                >
                  <MenuItem value="all">
                    <Checkbox checked={selectedGroups.length === machineGroups.length} />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {machineGroups.map((g) => (
                    <MenuItem key={g.name} value={g.name}>
                      <Checkbox checked={selectedGroups.includes(g.name)} />
                      <ListItemText primary={g.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {isProgramMethod || isMethod4 ? (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontSize: '13px', color: '#86868b' }}>Machines</InputLabel>
                <Select
                  value={programMachine || ''}
                  onChange={(e) => setProgramMachine(e.target.value)}
                  label="Machines"
                  sx={{
                    fontSize: '14px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      '&:hover': { borderColor: '#007AFF' },
                      '&.Mui-focused': { borderColor: '#007AFF', boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.1)' },
                    },
                  }}
                >
                  <MenuItem value="all">All Machines</MenuItem>
                  {availableMachines.map((machine) => (
                    <MenuItem key={machine} value={machine}>
                      {machine}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ fontSize: '13px', color: '#86868b' }}>Machines</InputLabel>
                  <Select
                    multiple
                    value={selectedMachines}
                    onChange={(e) => handleMachineChange(e.target.value)}
                    label="Machines"
                    renderValue={(selected) =>
                      isAllMachinesSelected ? 'All Machines' : selected.slice(0, 2).join(', ') + (selected.length > 2 ? '...' : '')
                    }
                    sx={{
                      fontSize: '14px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        '&:hover': { borderColor: '#007AFF' },
                        '&.Mui-focused': { borderColor: '#007AFF', boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.1)' },
                      },
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={isAllMachinesSelected} />
                      <ListItemText primary="All Machines" />
                    </MenuItem>
                    {availableMachines.map((machine) => (
                      <MenuItem key={machine} value={machine}>
                        <Checkbox checked={selectedMachines.includes(machine)} />
                        <ListItemText primary={machine} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Shift *</InputLabel>
                  <Select
                    multiple
                    value={selectedShifts}
                    onChange={(e) => handleShiftChange(e.target.value)}
                    label="Shift"
                    disabled={shifts.length === 0}
                    renderValue={(selected) => {
                      if (isAllShiftsSelected) return 'All Shifts';
                      if (selected.length === 0) return 'Select Shifts';
                      return selected.slice(0, 2).join(', ') + (selected.length > 2 ? '...' : '');
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={isAllShiftsSelected} />
                      <ListItemText primary="All Shifts" />
                    </MenuItem>
                    {shiftOptions}
                  </Select>
                </FormControl>
              </>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(val) => {
                  setStartDate(val);
                  const maxAllowed = val.add(1, 'month').isAfter(dayjs()) ? dayjs() : val.add(1, 'month');
                  if (endDate.isAfter(maxAllowed)) setEndDate(maxAllowed);
                }}
                format="DD-MM-YYYY"
                maxDate={dayjs()}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(val) => setEndDate(val)}
                format="DD-MM-YYYY"
                minDate={startDate}
                maxDate={startDate.add(1, 'month').isAfter(dayjs()) ? dayjs() : startDate.add(1, 'month')}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
            </LocalizationProvider>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                isLoading ||
                (isProgramMethod || isMethod4
                  ? programMachinesArray.length === 0
                  : selectedMachines.length === 0 || selectedShifts.length === 0)
              }
              sx={{
                minWidth: 120,
                height: 40,
                background: '#f47803ff',
                '&:hover': { background: '#e06d00ff' },
                '&.Mui-disabled': { background: '#f4780380' },
                textTransform: 'none',
                fontWeight: 'bold',
                gap: '8px',
              }}
            >
              {isLoading && <CircularProgress size={16} sx={{ color: '#fff' }} />}
              {isLoading ? 'Loading...' : 'Submit'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleExportOverviewPdf}
              disabled={isExporting || !appliedFilters}
              startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
              sx={{
                minWidth: 120,
                height: 40,
                color: '#f47803ff',
                borderColor: '#f47803ff',
                '&:hover': { borderColor: '#e06d00ff', background: 'rgba(244,120,3,0.06)' },
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </Button>

            {!isMethod4 && (
            <Button
              variant="outlined"
              onClick={handleExportOverviewPpt}
              disabled={isExportingPpt || !appliedFilters}
              startIcon={isExportingPpt ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
              sx={{
                minWidth: 120,
                height: 40,
                color: '#f47803ff',
                borderColor: '#f47803ff',
                '&:hover': { borderColor: '#e06d00ff', background: 'rgba(244,120,3,0.06)' },
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              {isExportingPpt ? 'Exporting...' : 'Export PPT'}
            </Button>
            )}

            <IconButton
              onClick={() => setCostSettingsOpen(true)}
              title="Machine / Component Cost Settings"
              sx={{
                height: 40,
                width: 40,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '10px',
                color: '#5f6368',
                '&:hover': { borderColor: '#f47803ff', color: '#f47803ff' },
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box sx={{ borderBottom: '1px solid #e0e0e0', background: '#ffffff', flexShrink: 0, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 44,
              color: '#5f6368',
            },
            '& .Mui-selected': { color: '#f47803ff !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#f47803ff' },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </Tabs>

        {ENABLED_METHODS.length > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: '12px', color: '#86868b' }}>Costing Method</span>
          <ToggleButtonGroup
            size="small"
            value={costingMethod}
            exclusive
            onChange={(e, val) => val && setCostingMethod(val)}
            sx={{
              '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '12px', py: 0.25, px: 1.5 },
              '& .Mui-selected': { color: '#f47803ff !important', background: 'rgba(244,120,3,0.08) !important' },
            }}
          >
            {ENABLED_METHODS.map((m) => (
              <ToggleButton key={m} value={m}>Method {m}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        )}
      </Box>

      <Dialog open={costSettingsOpen} onClose={() => setCostSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cost Settings</DialogTitle>
        <Box sx={{ borderBottom: '1px solid #e0e0e0', px: 3 }}>
          <Tabs
            value={costSettingsTab}
            onChange={(e, val) => setCostSettingsTab(val)}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40 },
              '& .Mui-selected': { color: '#f47803ff !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#f47803ff' },
            }}
          >
            <Tab value="machine" label="Machine Cost" />
            <Tab value="component" label={isProgramMethod ? 'Job Cost' : 'Component Cost'} />
          </Tabs>
        </Box>
        <DialogContent>
          {costSettingsTab === 'machine' && (
            <MachineHourRateSettings
              customerId={customerId}
              filters={isProgramMethod || isMethod4 ? programFilters : appliedFilters}
              defaultRate={defaultMachineHourRate}
            />
          )}
          {costSettingsTab === 'component' && (
            isProgramMethod ? (
              <JobCostSettings customerId={customerId} filters={programFilters} />
            ) : (
              <ComponentCostSettings customerId={customerId} filters={appliedFilters} />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCostSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {activeTab === 'overview' && (
          isMethod4 ? (
            <ProgramTransactionOverviewTab
              filters={programFilters}
              periodLabel={appliedFilters ? `${startDate.format('MMM DD')} – ${endDate.format('MMM DD')}` : ''}
              customerName={localStorage.getItem('customerTitle')}
            />
          ) : isProgramMethod ? (
            <ProgramOverviewTab
              filters={programFilters}
              periodLabel={appliedFilters ? `${startDate.format('MMM DD')} – ${endDate.format('MMM DD')}` : ''}
              customerName={localStorage.getItem('customerTitle')}
            />
          ) : (
            <OverviewTab
              filters={appliedFilters}
              periodLabel={appliedFilters ? `${startDate.format('MMM DD')} – ${endDate.format('MMM DD')}` : ''}
              customerName={localStorage.getItem('customerTitle')}
              method={costingMethod}
            />
          )
        )}
        {activeTab === 'table' && (
          isMethod4 ? (
            <ProgramTransactionReportTab filters={programFilters} />
          ) : isProgramMethod ? (
            <ProgramComponentReportTab filters={programFilters} method="1" shifts={shifts} />
          ) : (
            <ComponentReportTab filters={appliedFilters} method={costingMethod} />
          )
        )}
      </Box>
    </Box>
  );
}
